import {
  bubbleDayOfEmergencyGetByIdQuery,
  bubbleDayOfEmergencyGetCurrentQuery,
  bubbleDayOfEmergencyListParticipantsQuery,
  bubbleDayOfEmergencyListTimelineQuery,
  bubbleEmergencyDeclarationsListActiveQuery,
  type DayOfEmergencyDto,
  type DayOfEmergencyTimelineEntryDto,
  useBubbleDayOfEmergencyBatchSetSameStatus,
  useBubbleDayOfEmergencyGetById,
  useBubbleDayOfEmergencyListParticipants,
  useBubbleDayOfEmergencyListTimeline,
  useBubbleDayOfEmergencyPatchUser,
  useBubbleEmergencyDeclarationsListActive,
  useBubbleEmergencyMapLocationsList,
} from "@crowdcoursing/api/openapi";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useToast } from "~hooks/useToast";
import { type State } from "~hooks/useTypedRedux";
import { ActiveEmergencyDeclarationsSection } from "~ui/organisms/ActiveEmergencyDeclarationsSection/ActiveEmergencyDeclarationsSection";
import { Chat } from "~ui/organisms/Chat/Chat";
import { ParticipantRosterCard } from "~ui/organisms/ParticipantRosterCard/ParticipantRosterCard";

import { useSelector } from "../../../hooks";
import {
  EVERYONE_SUMMARY_GROUPS,
  fetchCurrentDayOfEmergencyForBubble,
  KNOWN_STATUS_VALUES,
  OTHER_LEGACY_SUMMARY_ID,
  participantDisplayName,
  participantMatchesSummaryFilter,
  type ParticipantRow,
  rosterMatchesSearch,
  STATUS_OPTIONS,
  statusLabelForKey,
  TIMELINE_ENTRY_KINDS,
  timelineEntryMatchesKindFilter,
  timelineKindLabel,
} from "./emergencyCommandCenterShared";
import { EmergencyParticipantStatusDialog } from "./EmergencyParticipantStatusDialog";

type TabKey = "everyone" | "report" | "timeline" | "map" | "chat";

export function EmergencyCommandCenterPage() {
  const { schoolId: bubbleId = "", dayOfEmergencyId: routeDayId } = useParams<{
    schoolId: string;
    dayOfEmergencyId?: string;
  }>();
  const isSpecificDay = Boolean(routeDayId);
  const currentUserId = useSelector((s: State) => s.user?.id) ?? "";
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>("everyone");
  const [statusKey, setStatusKey] = useState<string>("safe");
  const [mapLocationId, setMapLocationId] = useState<string>("");
  const [bulkSearch, setBulkSearch] = useState("");
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState<string[]>([]);
  const [rosterSearch, setRosterSearch] = useState("");
  /** Empty = show all statuses; otherwise show people in any selected summary bucket (OR). */
  const [summaryFilterIds, setSummaryFilterIds] = useState<string[]>([]);
  /** Empty = all timeline kinds; otherwise filter to selected kinds (OR). */
  const [timelineKindFilterIds, setTimelineKindFilterIds] = useState<string[]>([]);
  const [participantDialog, setParticipantDialog] = useState<ParticipantRow | null>(null);
  const [participantDialogError, setParticipantDialogError] = useState<string | null>(null);

  const currentDayQuery = useQuery<DayOfEmergencyDto | null>({
    ...bubbleDayOfEmergencyGetCurrentQuery({ pathParams: { bubbleId } }),
    enabled: Boolean(bubbleId) && !isSpecificDay,
    queryFn: ({ signal }) => fetchCurrentDayOfEmergencyForBubble(bubbleId, signal),
    refetchInterval: 30_000,
    retry: false,
  });

  const dayByIdQuery = useBubbleDayOfEmergencyGetById(
    { pathParams: { bubbleId, dayOfEmergencyId: routeDayId ?? "" } },
    {
      enabled: Boolean(bubbleId && routeDayId),
      refetchInterval: 30_000,
      retry: false,
    },
  );

  const dayQuery = isSpecificDay ? dayByIdQuery : currentDayQuery;

  const dayId = dayQuery.data?.id;
  const hasDay = Boolean(dayId);

  const participantsQuery = useBubbleDayOfEmergencyListParticipants(
    { pathParams: { bubbleId, dayOfEmergencyId: dayId ?? "" } },
    {
      enabled: Boolean(bubbleId && dayId),
      refetchInterval: tab === "everyone" || tab === "report" ? 10_000 : false,
    },
  );

  const timelineQuery = useBubbleDayOfEmergencyListTimeline(
    { pathParams: { bubbleId, dayOfEmergencyId: dayId ?? "" } },
    {
      enabled: Boolean(bubbleId && dayId) && tab === "timeline",
      refetchInterval: tab === "timeline" ? 15_000 : false,
      retry: false,
    },
  );

  const mapLocationsQuery = useBubbleEmergencyMapLocationsList(
    { pathParams: { bubbleId } },
    { enabled: Boolean(bubbleId && dayId) },
  );

  const activeDeclarationsQuery = useBubbleEmergencyDeclarationsListActive(
    { pathParams: { bubbleId } },
    { enabled: Boolean(bubbleId), refetchInterval: 15_000 },
  );

  const invalidateParticipantsAndTimeline = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: bubbleDayOfEmergencyListParticipantsQuery({
        pathParams: { bubbleId, dayOfEmergencyId: dayId ?? "" },
      }).queryKey,
    });
    if (dayId) {
      await queryClient.invalidateQueries({
        queryKey: bubbleDayOfEmergencyListTimelineQuery({
          pathParams: { bubbleId, dayOfEmergencyId: dayId },
        }).queryKey,
      });
    }
  }, [bubbleId, dayId, queryClient]);

  const batchSetSameStatus = useBubbleDayOfEmergencyBatchSetSameStatus({
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not update selected participants.");
    },
    onSuccess: async (_, variables) => {
      const n = variables.body?.userIds?.length ?? 0;
      toast.success(n === 1 ? "Updated 1 participant." : `Updated ${n} participants.`);
      setBulkSelectedUserIds([]);
      await invalidateParticipantsAndTimeline();
    },
  });

  const patchOtherParticipant = useBubbleDayOfEmergencyPatchUser({
    onError: (err) => {
      setParticipantDialogError(err instanceof Error ? err.message : "Could not update status.");
    },
    onSuccess: async () => {
      setParticipantDialogError(null);
      setParticipantDialog(null);
      await queryClient.invalidateQueries({
        queryKey: bubbleDayOfEmergencyListParticipantsQuery({
          pathParams: { bubbleId, dayOfEmergencyId: dayId ?? "" },
        }).queryKey,
      });
      if (dayId) {
        await queryClient.invalidateQueries({
          queryKey: bubbleDayOfEmergencyListTimelineQuery({
            pathParams: { bubbleId, dayOfEmergencyId: dayId },
          }).queryKey,
        });
      }
    },
  });

  const handleSaveParticipantStatus = useCallback(
    async (payload: { bubbleMapLocationId: string | null; statusKey: string }) => {
      if (!bubbleId || !dayId || !participantDialog?.userId) {
        return;
      }
      setParticipantDialogError(null);
      await patchOtherParticipant.mutateAsync({
        body: {
          bubbleMapLocationId: payload.bubbleMapLocationId ?? undefined,
          statusKey: payload.statusKey,
        },
        pathParams: {
          bubbleId,
          dayOfEmergencyId: dayId,
          userId: String(participantDialog.userId),
        },
      });
    },
    [bubbleId, dayId, participantDialog?.userId, patchOtherParticipant],
  );

  const invalidateDayAndParticipants = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: bubbleDayOfEmergencyGetCurrentQuery({ pathParams: { bubbleId } }).queryKey,
    });
    if (routeDayId) {
      await queryClient.invalidateQueries({
        queryKey: bubbleDayOfEmergencyGetByIdQuery({
          pathParams: { bubbleId, dayOfEmergencyId: routeDayId },
        }).queryKey,
      });
    }
    if (dayId) {
      await queryClient.invalidateQueries({
        queryKey: bubbleDayOfEmergencyListParticipantsQuery({
          pathParams: { bubbleId, dayOfEmergencyId: dayId },
        }).queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: bubbleDayOfEmergencyListTimelineQuery({
          pathParams: { bubbleId, dayOfEmergencyId: dayId },
        }).queryKey,
      });
    }
    await queryClient.invalidateQueries({
      queryKey: bubbleEmergencyDeclarationsListActiveQuery({ pathParams: { bubbleId } }).queryKey,
    });
  }, [bubbleId, dayId, queryClient, routeDayId]);

  const rosterRows = useMemo(() => (participantsQuery.data ?? []) as ParticipantRow[], [participantsQuery.data]);

  const bulkFilteredRows = useMemo(
    () => rosterRows.filter((p) => rosterMatchesSearch(p, bulkSearch)),
    [rosterRows, bulkSearch],
  );

  useEffect(() => {
    const valid = new Set(rosterRows.map((p) => String(p.userId)));
    setBulkSelectedUserIds((prev) => prev.filter((id) => valid.has(id)));
  }, [rosterRows]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const o of STATUS_OPTIONS) {
      counts[o.value] = 0;
    }
    let otherUnknownStatuses = 0;
    for (const raw of rosterRows) {
      const key = raw.statusKey ?? "not-reported";
      if (KNOWN_STATUS_VALUES.has(key)) {
        counts[key] += 1;
      } else {
        otherUnknownStatuses += 1;
      }
    }
    return { counts, otherUnknownStatuses };
  }, [rosterRows]);

  const toggleSummaryFilter = useCallback((id: string) => {
    setSummaryFilterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const filteredRoster = useMemo(() => {
    return rosterRows
      .filter((p) => rosterMatchesSearch(p, rosterSearch))
      .filter((p) => participantMatchesSummaryFilter(p, summaryFilterIds));
  }, [rosterRows, rosterSearch, summaryFilterIds]);

  const timelineEntriesNewestFirst = useMemo(() => {
    const rows = timelineQuery.data ?? [];
    return [...rows].sort((a, b) => {
      const ta = new Date(a.occurredAtUtc ?? 0).getTime();
      const tb = new Date(b.occurredAtUtc ?? 0).getTime();
      return tb - ta;
    });
  }, [timelineQuery.data]);

  const toggleTimelineKindFilter = useCallback((kind: string) => {
    setTimelineKindFilterIds((prev) => (prev.includes(kind) ? prev.filter((x) => x !== kind) : [...prev, kind]));
  }, []);

  const filteredTimelineEntries = useMemo(() => {
    return timelineEntriesNewestFirst.filter((e) => timelineEntryMatchesKindFilter(e.kind, timelineKindFilterIds));
  }, [timelineEntriesNewestFirst, timelineKindFilterIds]);

  const toggleBulkUser = useCallback((userId: string) => {
    setBulkSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  }, []);

  const clearBulkSelection = useCallback(() => {
    setBulkSelectedUserIds([]);
  }, []);

  const handleSubmitBulkSameStatus = useCallback(() => {
    if (!bubbleId || !dayId || bulkSelectedUserIds.length === 0) {
      return;
    }
    batchSetSameStatus.mutate({
      body: {
        bubbleMapLocationId: mapLocationId ? mapLocationId : null,
        statusKey,
        userIds: bulkSelectedUserIds,
      },
      pathParams: { bubbleId, dayOfEmergencyId: dayId },
    });
  }, [batchSetSameStatus, bubbleId, bulkSelectedUserIds, dayId, mapLocationId, statusKey]);

  const noDayYet = !isSpecificDay && dayQuery.isSuccess && !hasDay;
  const loadFailed = dayQuery.isError;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { md: 3, xs: 2 } }}>
      <Stack alignItems="center" direction="row" flexWrap="wrap" justifyContent="space-between" sx={{ gap: 1, mb: 2 }}>
        <Typography component="h1" variant="h5">
          Emergency command center
        </Typography>
        {hasDay ? (
          <Button onClick={() => void invalidateDayAndParticipants()} size="small" variant="outlined">
            Refresh data
          </Button>
        ) : null}
      </Stack>

      <ActiveEmergencyDeclarationsSection declarations={activeDeclarationsQuery.data ?? []} />

      {dayQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {loadFailed ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {isSpecificDay
            ? "Could not load this emergency day. It may have been removed or you may not have access."
            : "Could not load today's emergency day. Try again or confirm you have access to the emergency plan for this school."}
        </Alert>
      ) : null}

      {noDayYet ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No emergency day record exists yet for today (UTC). Use <strong>Quick Response</strong> from the header to
          dispatch an emergency; then return here for roster and status updates.
        </Alert>
      ) : null}

      {hasDay && dayQuery.data ? (
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="caption">
          Date: {String(dayQuery.data.utcCalendarDate)}
        </Typography>
      ) : null}

      {hasDay ? (
        <>
          <Tabs
            allowScrollButtonsMobile
            onChange={(_, v) => setTab(v as TabKey)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            value={tab}
            variant="scrollable"
          >
            <Tab label="Everyone" value="everyone" />
            <Tab label="Report status" value="report" />
            <Tab label="Timeline" value="timeline" />
            <Tab label="Map" value="map" />
            <Tab label="Live chat" value="chat" />
          </Tabs>

          {tab === "everyone" ? (
            <Stack spacing={2}>
              {participantsQuery.isLoading ? (
                <CircularProgress size={32} />
              ) : participantsQuery.isError ? (
                <Alert severity="error">Could not load participants.</Alert>
              ) : (
                <>
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                    <Typography color="text.secondary" component="span" variant="caption">
                      Click summary cards to filter the roster (select several to combine). Click again to remove a
                      filter.
                    </Typography>
                    {summaryFilterIds.length > 0 ? (
                      <Button onClick={() => setSummaryFilterIds([])} size="small" variant="text">
                        Clear status filters
                      </Button>
                    ) : null}
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.5,
                      gridTemplateColumns: {
                        md: "repeat(4, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        xl: "repeat(6, minmax(0, 1fr))",
                        xs: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {EVERYONE_SUMMARY_GROUPS.map((g) => {
                      const selected = summaryFilterIds.includes(g.id);
                      return (
                        <Card
                          key={g.id}
                          variant="outlined"
                          sx={{
                            bgcolor: selected ? "action.selected" : undefined,
                            borderColor: selected ? "primary.main" : undefined,
                            borderWidth: selected ? 2 : 1,
                          }}
                        >
                          <CardActionArea
                            aria-label={`Filter by ${g.label}`}
                            aria-pressed={selected}
                            onClick={() => toggleSummaryFilter(g.id)}
                            sx={{ borderRadius: 1 }}
                          >
                            <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                              <Typography color="text.secondary" component="div" variant="caption">
                                {g.label}
                              </Typography>
                              <Typography component="div" sx={{ mt: 0.5 }} variant="h5">
                                {g.statusValues.reduce((sum, v) => sum + (statusCounts.counts[v] ?? 0), 0)}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                    {statusCounts.otherUnknownStatuses > 0 ? (
                      <Card
                        key={OTHER_LEGACY_SUMMARY_ID}
                        variant="outlined"
                        sx={{
                          bgcolor: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? "action.selected" : undefined,
                          borderColor: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? "primary.main" : undefined,
                          borderWidth: summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID) ? 2 : 1,
                        }}
                      >
                        <CardActionArea
                          aria-label="Filter by other or legacy status"
                          aria-pressed={summaryFilterIds.includes(OTHER_LEGACY_SUMMARY_ID)}
                          onClick={() => toggleSummaryFilter(OTHER_LEGACY_SUMMARY_ID)}
                          sx={{ borderRadius: 1 }}
                        >
                          <CardContent sx={{ "&:last-child": { pb: 1.5 }, py: 1.5 }}>
                            <Typography color="text.secondary" component="div" variant="caption">
                              Other / legacy
                            </Typography>
                            <Typography component="div" sx={{ mt: 0.5 }} variant="h5">
                              {statusCounts.otherUnknownStatuses}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    ) : null}
                  </Box>

                  <TextField
                    fullWidth
                    onChange={(e) => setRosterSearch(e.target.value)}
                    placeholder="Search by name, phone, or role"
                    size="small"
                    value={rosterSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography color="text.secondary" variant="caption">
                    Showing {filteredRoster.length} of {rosterRows.length} people
                    {rosterSearch.trim() || summaryFilterIds.length > 0 ? " (filtered)" : ""}
                  </Typography>

                  {filteredRoster.length === 0 && rosterRows.length > 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No one matches your filters. Try a different search, adjust the status cards above, or clear
                      filters.
                    </Typography>
                  ) : null}

                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        md: "repeat(2, minmax(0, 1fr))",
                        xl: "repeat(4, minmax(0, 1fr))",
                        xs: "minmax(0, 1fr)",
                      },
                    }}
                  >
                    {filteredRoster.map((row) => (
                      <ParticipantRosterCard
                        key={row.userId}
                        currentUserId={currentUserId}
                        participant={row}
                        onSelect={(p) => {
                          setParticipantDialog(p);
                          setParticipantDialogError(null);
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Stack>
          ) : null}

          {tab === "report" ? (
            <Stack maxWidth={640} spacing={2}>
              <Button
                disabled={bulkSelectedUserIds.length === 0 || batchSetSameStatus.isPending}
                onClick={handleSubmitBulkSameStatus}
                variant="contained"
              >
                {batchSetSameStatus.isPending
                  ? "Applying…"
                  : bulkSelectedUserIds.length === 0
                    ? "Apply status to selected users"
                    : `Apply status to ${bulkSelectedUserIds.length} user${bulkSelectedUserIds.length === 1 ? "" : "s"}`}
              </Button>
              <Typography variant="body2">
                Set status and optional map location, search the roster, select everyone who should receive this
                update—including yourself when your status should match—then apply. The Everyone tab updates after a
                successful save.
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  label="Status"
                  labelId="status-label"
                  onChange={(e) => setStatusKey(String(e.target.value))}
                  value={statusKey}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="loc-label">Map location (optional)</InputLabel>
                <Select
                  label="Map location (optional)"
                  labelId="loc-label"
                  onChange={(e) => setMapLocationId(String(e.target.value))}
                  value={mapLocationId}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(mapLocationsQuery.data ?? [])
                    .filter((l) => l.isActive)
                    .map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                InputProps={{
                  endAdornment:
                    bulkSearch.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton aria-label="Clear search" edge="end" onClick={() => setBulkSearch("")} size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setBulkSearch(e.target.value)}
                placeholder="Search by name, phone, or role"
                size="small"
                value={bulkSearch}
              />
              <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                <Button
                  disabled={bulkSelectedUserIds.length === 0}
                  onClick={clearBulkSelection}
                  size="small"
                  variant="text"
                >
                  Clear selection
                </Button>
                <Typography color="text.secondary" variant="caption">
                  {bulkSelectedUserIds.length} selected
                </Typography>
              </Stack>
              {participantsQuery.isLoading ? (
                <CircularProgress size={28} />
              ) : bulkFilteredRows.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No participants match your search.
                </Typography>
              ) : (
                <FormGroup
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    maxHeight: 320,
                    overflow: "auto",
                    p: 1,
                  }}
                >
                  {bulkFilteredRows.map((p) => {
                    const uid = String(p.userId ?? "");
                    if (!uid) {
                      return null;
                    }
                    const checked = bulkSelectedUserIds.includes(uid);
                    return (
                      <FormControlLabel
                        control={<Checkbox checked={checked} onChange={() => toggleBulkUser(uid)} size="small" />}
                        key={uid}
                        label={
                          <Box>
                            <Typography component="span" variant="body2">
                              {participantDisplayName(p)}
                            </Typography>
                            <Typography color="text.secondary" component="span" display="block" variant="caption">
                              {statusLabelForKey(p.statusKey)}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </FormGroup>
              )}
              <Button
                disabled={bulkSelectedUserIds.length === 0 || batchSetSameStatus.isPending}
                onClick={handleSubmitBulkSameStatus}
                variant="contained"
              >
                {batchSetSameStatus.isPending
                  ? "Applying…"
                  : bulkSelectedUserIds.length === 0
                    ? "Apply status to selected users"
                    : `Apply status to ${bulkSelectedUserIds.length} user${bulkSelectedUserIds.length === 1 ? "" : "s"}`}
              </Button>
            </Stack>
          ) : null}

          {tab === "timeline" ? (
            <Stack spacing={2}>
              <Typography color="text.secondary" variant="body2">
                Activity for this reunification day (newest first): dispatches, status updates, and emergency plan
                access via shared links.
              </Typography>
              {timelineQuery.isLoading ? (
                <CircularProgress size={32} />
              ) : timelineQuery.isError ? (
                <Alert severity="error">Could not load timeline.</Alert>
              ) : timelineEntriesNewestFirst.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No recorded activity yet for this day.
                </Typography>
              ) : (
                <>
                  <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
                    <Typography color="text.secondary" component="span" variant="caption">
                      Filter by activity type (select one or more). Click again to clear a type.
                    </Typography>
                    {timelineKindFilterIds.length > 0 ? (
                      <Button onClick={() => setTimelineKindFilterIds([])} size="small" variant="text">
                        Clear type filters
                      </Button>
                    ) : null}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {TIMELINE_ENTRY_KINDS.map((kind) => {
                      const selected = timelineKindFilterIds.includes(kind);
                      return (
                        <Chip
                          key={kind}
                          clickable
                          color={selected ? "primary" : "default"}
                          label={timelineKindLabel(kind)}
                          onClick={() => toggleTimelineKindFilter(kind)}
                          size="small"
                          variant={selected ? "filled" : "outlined"}
                        />
                      );
                    })}
                  </Stack>
                  <Typography color="text.secondary" variant="caption">
                    Showing {filteredTimelineEntries.length} of {timelineEntriesNewestFirst.length} activities
                    {timelineKindFilterIds.length > 0 ? " (filtered)" : ""}
                  </Typography>
                  {filteredTimelineEntries.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      No activities match the selected types. Clear a filter or choose different types.
                    </Typography>
                  ) : (
                    <Stack component="ol" sx={{ listStyle: "none", m: 0, p: 0 }} spacing={0}>
                      {filteredTimelineEntries.map((e: DayOfEmergencyTimelineEntryDto, i: number) => (
                        <Box
                          component="li"
                          key={`${e.kind}-${e.occurredAtUtc}-${i}`}
                          sx={{
                            "&:last-child": { pb: 0 },
                            borderColor: "divider",
                            borderLeft: 2,
                            ml: 0.5,
                            pb: 2,
                            pl: 2,
                          }}
                        >
                          <Typography color="text.secondary" variant="caption">
                            {new Date(e.occurredAtUtc ?? "").toLocaleString()}
                          </Typography>
                          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                            <Chip label={timelineKindLabel(e.kind ?? "")} size="small" variant="outlined" />
                            {e.actorDisplayName ? (
                              <Typography color="text.secondary" variant="caption">
                                {e.actorDisplayName}
                              </Typography>
                            ) : null}
                          </Stack>
                          <Typography sx={{ mt: 1 }} variant="body2">
                            {e.summary}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          ) : null}

          {tab === "map" ? (
            <Alert severity="info">
              Map view is coming soon. Map locations are configured under Emergency Management; this tab will show pins
              on a map in a later release.
            </Alert>
          ) : null}

          {tab === "chat" ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                height: "min(70vh, 560px)",
                minHeight: 360,
                overflow: "hidden",
              }}
            >
              {dayId ? <Chat dayOfEmergencyId={dayId} /> : null}
            </Box>
          ) : null}
        </>
      ) : null}

      <EmergencyParticipantStatusDialog
        errorMessage={participantDialogError}
        mapLocations={(mapLocationsQuery.data ?? []).map((l) => ({
          id: String(l.id),
          isActive: l.isActive,
          label: l.label ?? "",
        }))}
        mapLocationsLoading={mapLocationsQuery.isLoading}
        onClose={() => {
          setParticipantDialog(null);
          setParticipantDialogError(null);
        }}
        onSave={handleSaveParticipantStatus}
        open={Boolean(participantDialog)}
        participant={participantDialog}
        saving={patchOtherParticipant.isPending}
        title="Update participant status"
      />
    </Box>
  );
}
